import { useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_DELAY = 6000

interface UseUserInteractionsProps {
  timedActions: () => void
  enableConditionFn: () => boolean
}

export const useUserInteractions = ({
  timedActions,
  enableConditionFn,
}: UseUserInteractionsProps) => {
  const [timerElapsed, setTimerElapsed] = useState(false)
  const [userScrolled, setUserScrolled] = useState(false)
  const [userClicked, setUserClicked] = useState(false)

  const userDidScroll = useCallback(() => {
    setUserScrolled(true)
  }, [setUserScrolled])

  const userDidClick = useCallback(() => {
    setUserClicked(true)
  }, [setUserClicked])

  const timerRef: {
    current: null | ReturnType<typeof setTimeout>
  } = useRef(null)

  const handleActions = useCallback(() => {
    if (!userClicked && timerElapsed && userScrolled) {
      timedActions()

      window.removeEventListener('scroll', userDidScroll)
      window.removeEventListener('click', userDidClick)
    }
  }, [
    timerElapsed,
    userScrolled,
    userClicked,
    timedActions,
    userDidScroll,
    userDidClick,
  ])

  const setUserInteractions = useCallback(() => {
    window.addEventListener('scroll', userDidScroll)
    window.addEventListener('click', userDidClick)
  }, [userDidScroll, userDidClick])

  useEffect(() => {
    if (!enableConditionFn()) {
      timerRef.current = setTimeout(() => {
        setTimerElapsed(true)
      }, DEFAULT_DELAY)

      setUserInteractions()

      handleActions()
    }

    return () => {
      clearTimeout(timerRef.current as ReturnType<typeof setTimeout>)
    }
  }, [setUserInteractions, handleActions, enableConditionFn])
}
